import { useState, type FormEvent } from 'react';
import { BrandMark } from '../components/BrandMark';
import { ErrorNotice } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { post, setCsrf } from '../lib/api';
import type { User } from '../types';

export function LandingAuth({ onAuthenticated }: { onAuthenticated: (user: User) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const form = new FormData(event.currentTarget);
    try {
      const payload = Object.fromEntries(form.entries());
      const result = await post<{ user: User; csrfToken: string }>(`/api/auth/${mode}`, payload);
      setCsrf(result.csrfToken);
      onAuthenticated(result.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Tidak dapat melanjutkan.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="landing">
      <section className="landing-story">
        <BrandMark />
        <div className="story-copy">
          <span className="eyebrow">Private academic workspace</span>
          <h1>Skripsi yang tetap utuh, dari gagasan hingga dokumen akhir.</h1>
          <p>
            Susun penelitian dalam struktur yang rapi, lindungi fakta penting, dan ikuti pedoman
            kampus tanpa mengubah semuanya secara manual.
          </p>
          <div className="story-points">
            <span>
              <SenoIcon name="validasi" /> Fakta penelitian terlindungi
            </span>
            <span>
              <SenoIcon name="pedoman" /> Pedoman menjadi aturan aktif
            </span>
            <span>
              <SenoIcon name="history" /> Autosave dan riwayat versi
            </span>
          </div>
        </div>
        <p className="story-quote">“Setiap ide besar berawal dari langkah yang terarah.”</p>
      </section>
      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="auth-box">
          <div className="mobile-brand">
            <BrandMark />
          </div>
          <span className="eyebrow">Mulai ruang kerja Anda</span>
          <h2 id="auth-title">
            {mode === 'register' ? 'Buat akun SenoSkripsi' : 'Selamat datang kembali'}
          </h2>
          <p>
            {mode === 'register'
              ? 'Satu akun untuk semua proyek akademik Anda.'
              : 'Lanjutkan dari versi terakhir yang tersimpan.'}
          </p>
          <div className="auth-tabs" role="tablist" aria-label="Pilihan autentikasi">
            <button
              type="button"
              className={mode === 'register' ? 'active' : ''}
              onClick={() => setMode('register')}
            >
              Daftar
            </button>
            <button
              type="button"
              className={mode === 'login' ? 'active' : ''}
              onClick={() => setMode('login')}
            >
              Masuk
            </button>
          </div>
          {error ? <ErrorNotice message={error} /> : null}
          <form onSubmit={submit} className="auth-form">
            {mode === 'register' ? (
              <label>
                Nama lengkap
                <input
                  name="displayName"
                  autoComplete="name"
                  minLength={2}
                  required
                  placeholder="Nama Anda"
                />
              </label>
            ) : null}
            <label>
              Email
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="nama@email.com"
              />
            </label>
            <label>
              Kata sandi
              <input
                name="password"
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                minLength={mode === 'register' ? 10 : 1}
                required
                placeholder="••••••••••"
              />
              {mode === 'register' ? (
                <small>Minimal 10 karakter, huruf besar, huruf kecil, dan angka.</small>
              ) : null}
            </label>
            {mode === 'register' ? (
              <div className="registration-consent">
                <label>
                  <input name="termsAccepted" type="checkbox" required />
                  <span>
                    Saya menyetujui <a href="/terms">Ketentuan Layanan</a>.
                  </span>
                </label>
                <label>
                  <input name="privacyAccepted" type="checkbox" required />
                  <span>
                    Saya telah membaca <a href="/privacy">Kebijakan Privasi</a>.
                  </span>
                </label>
              </div>
            ) : null}
            <button className="button button-primary button-wide" disabled={busy}>
              {busy
                ? 'Menyiapkan…'
                : mode === 'register'
                  ? 'Buat ruang kerja'
                  : 'Masuk ke ruang kerja'}
            </button>
          </form>
          <p className="auth-foot">
            Data proyek bersifat privat dan setiap akses diverifikasi di server.
          </p>
          <nav className="auth-legal" aria-label="Kebijakan">
            <a href="/ai-disclosure">Pengungkapan AI</a>
            <a href="/refund-policy">Pembayaran & refund</a>
          </nav>
        </div>
      </section>
    </main>
  );
}
